import { Loader2, RefreshCw, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  useGarminConnections,
  useGarminBackfill,
  useHrDistributionBackfill,
} from "@/hooks/api/useGarminBackfill";

export function GarminTab() {
  const { data: connections, isLoading } = useGarminConnections();
  const backfill = useGarminBackfill();
  const hrDistBackfill = useHrDistributionBackfill();

  const handleBackfillAll = async () => {
    if (!connections?.length) return;

    const ids = connections.map((c) => c.authUserId);
    const confirmed = window.confirm(
      `This will enqueue 30-day backfill jobs for ${ids.length} connection${ids.length > 1 ? "s" : ""}. Users without the garmin feature flag or with expired tokens will be skipped. Continue?`
    );
    if (!confirmed) return;

    try {
      const result = await backfill.mutateAsync({ authUserIds: ids, days: 30 });
      toast.success(
        `Enqueued ${result.enqueued.length} backfill jobs` +
          (result.skipped.length > 0
            ? `, ${result.skipped.length} skipped`
            : "")
      );
    } catch (err: any) {
      toast.error(err.message || "Backfill failed");
    }
  };

  const handleHrDistributionBackfill = async () => {
    const confirmed = window.confirm(
      "This will scan for all users with missing hrDistribution data and enqueue per-user backfill jobs. Continue?"
    );
    if (!confirmed) return;

    try {
      const result = await hrDistBackfill.mutateAsync();
      toast.success(result.message || "HR distribution backfill scan enqueued");
    } catch (err: any) {
      toast.error(err.message || "HR distribution backfill failed");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Garmin Backfill</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-admin-text-secondary mb-3">
            Enqueue 30-day backfill jobs for all Garmin connections with the garmin feature flag enabled.
            Each job runs in the background (token refresh + data request).
            Users with expired refresh tokens are skipped.
          </p>

          <Button
            onClick={handleBackfillAll}
            disabled={!connections?.length || backfill.isPending}
          >
            {backfill.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Backfill all {connections?.length ?? 0} connections (30 days)
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">HR Distribution Backfill</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-admin-text-secondary mb-3">
            Scan for daily snapshots missing hrDistribution and rebuild them from raw heart rate samples.
            Needed after the schema migration from fixed HR zones to histogram-based distribution.
            Safe to re-run — only processes snapshots that still have NULL hrDistribution.
          </p>

          <Button
            onClick={handleHrDistributionBackfill}
            disabled={hrDistBackfill.isPending}
          >
            {hrDistBackfill.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <BarChart3 className="h-4 w-4 mr-2" />
            )}
            Scan &amp; backfill missing hrDistribution
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
